import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Trash2, Loader2, Search, FlaskConical } from "lucide-react";
import { getHorses, startSellerPreview, listSellerDrafts, deleteSellerDraft } from "@/lib/api";
import { statusLabel, statusTone } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MobileMenu, MobileTabBar } from "@/components/AppNav";
import { AppHeader } from "@/components/brand/AppHeader";

export default function SellerPreviewPicker() {
  const navigate = useNavigate();
  const [horses, setHorses] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const [h, d] = await Promise.all([getHorses().catch(() => []), listSellerDrafts().catch(() => [])]);
    setHorses(h);
    setDrafts(d);
  };
  useEffect(() => { load(); }, []);

  const start = async (horseId) => {
    setBusy(horseId);
    try {
      const draft = await startSellerPreview(horseId);
      navigate(`/seller-preview/${draft.id}`);
    } catch {
      toast.error("Could not start seller preview");
      setBusy(null);
    }
  };

  const removeDraft = async (id, e) => {
    e.stopPropagation();
    try { await deleteSellerDraft(id); toast.success("Test draft reset"); load(); }
    catch { toast.error("Could not delete draft"); }
  };

  const filtered = (horses || []).filter((h) =>
    (h.horse_info?.horse_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24 md:pb-0 overflow-x-clip">
      <div data-testid="preview-mode-banner" className="bg-amber-800 text-white text-xs sm:text-sm font-medium px-4 py-2 flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5"><FlaskConical className="h-4 w-4" /> Seller Preview Mode — test only. Nothing here changes Smartsheet or live ads.</span>
        <Button data-testid="exit-preview-top" size="sm" variant="secondary" onClick={() => navigate("/")}
          className="rounded-full h-9 shrink-0 bg-white/15 hover:bg-white/25 text-white border-0">Exit Preview</Button>
      </div>
      <AppHeader title="Preview as Seller" eyebrow="Seller Preview (Test Mode)" onBack={() => navigate("/")} backTestId="back-to-horses" width="max-w-5xl">
          <MobileMenu />
      </AppHeader>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-6 space-y-8">
        <p className="text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          Pick an existing horse to simulate a seller completing its listing. Your edits go into an
          <strong> isolated test draft</strong> — the original Smartsheet record is never changed.
        </p>

        {drafts.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-stone-900 mb-3">Your Test Drafts</h2>
            <div className="space-y-2" data-testid="test-drafts-list">
              {drafts.map((d) => (
                <div key={d.id} data-testid={`draft-${d.id}`}
                  onClick={() => navigate(`/seller-preview/${d.id}`)}
                  className="cursor-pointer bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-center justify-between hover:border-amber-300">
                  <div className="min-w-0">
                    <span className="font-medium text-stone-900 block sm:inline truncate">{d.horse_info?.horse_name || "Untitled"}</span>
                    <span className="text-xs text-stone-400 sm:ml-2">from {d.origin_horse_id ? "Smartsheet horse" : "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <Badge variant="outline" className={`rounded-full ${statusTone(d.listing_status)}`}>{statusLabel(d.listing_status)}</Badge>
                    <Button data-testid={`reset-draft-${d.id}`} size="icon" variant="ghost"
                      onClick={(e) => removeDraft(d.id, e)} className="h-11 w-11 rounded-full text-stone-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2 sm:gap-3">
            <h2 className="text-sm font-semibold text-stone-900">Choose a Horse</h2>
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input data-testid="horse-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name" className="pl-9 h-11 sm:h-10 text-base sm:text-sm border-stone-200 focus-visible:ring-amber-800" />
            </div>
          </div>
          {horses === null ? (
            <div className="grid sm:grid-cols-2 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="horse-picker-grid">
              {filtered.slice(0, 60).map((h) => (
                <div key={h.id} className="bg-white border border-stone-200 rounded-xl px-4 py-3 flex items-center justify-between">
                  <div className="min-w-0 flex-1 mr-2">
                    <div className="font-medium text-stone-900 truncate">{h.horse_info?.horse_name || "Untitled"}</div>
                    <div className="text-xs text-stone-500">{h.gender_display || h.horse_info?.gender || ""} {h.age_display ? `· ${h.age_display}` : ""}</div>
                  </div>
                  <Button data-testid={`preview-horse-${h.id}`} size="sm" disabled={busy === h.id}
                    onClick={() => start(h.id)}
                    className="h-11 sm:h-9 rounded-full bg-amber-800 hover:bg-amber-900 text-white shrink-0">
                    {busy === h.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1.5" /> Preview</>}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <MobileTabBar />
    </div>
  );
}
